import { OrganizationsService } from 'src/app/Services/organizations.service';
import { LevelDesignerComponent } from '../../level-designer/level-designer.component';
import { SelectInput } from './select-input';
import { ApiService } from 'src/app/Services/api.service';
import { PropertyEditor } from '../property-editor';

export class SearchableSelectInput extends SelectInput {
    private searchTimeout: any;
    private minSearchLength: number;
    private originalDisplayValue: string = '';  // Initialize with empty string
    private isLoading: boolean = false;
    private spinnerAngle: number = 0;
    private spinnerAnimationFrame: number | null = null;
    private focusValue: any = undefined;
    public apiService: ApiService;
    public url: string;
    public get currentDisplayValue(): string {
        return this.displayValue;
    }

    constructor(
        ctx: CanvasRenderingContext2D,
        x: number,
        y: number,
        width: number,
        height: number,
        value: string,
        isActive: boolean,
        apiService: ApiService,
        url: string,
        idProperty: string,
        labelProperty: string,
        minSearchLength: number = 2,
        levelDesigner: LevelDesignerComponent,
        propertyEditor: PropertyEditor,
        organizationService: OrganizationsService,
        initialDisplayValue?: string
    ) {
        super(ctx, x, y, width, height, value, isActive, [], levelDesigner, propertyEditor, true);
        this.apiService = apiService;
        this.url = url;
        this.idProperty = idProperty;
        this.labelProperty = labelProperty;
        this.minSearchLength = minSearchLength;
        
        if (initialDisplayValue) {
            this.displayValue = initialDisplayValue;
            this.searchText = initialDisplayValue;
            this.originalDisplayValue = initialDisplayValue;
        }
    }

    public setInitialDisplayValue(value: string): void {
        this.displayValue = value;
        this.searchText = value;
        this.originalDisplayValue = value;
    }

    override draw(): void {
        super.draw();

        if (!this.dropdownVisible) return;

        if (this.isLoading && this.options.length === 0) {
            this.drawSpinnerPanel();
        } else if (!this.isLoading && this.searchText.length >= this.minSearchLength && this.options.length === 0) {
            this.ctx.fillStyle = '#666666';
            this.ctx.font = '10px Arial';
            this.ctx.fillText('No results found',
                this.x + 5, this.y + this.height + 15);
        }
    }

    private drawSpinnerPanel(): void {
        const panelHeight = 30;
        const panelY = this.y + this.height;

        this.ctx.fillStyle = '#FFFFFF';
        this.ctx.strokeStyle = '#CCCCCC';
        this.ctx.lineWidth = 1;
        this.ctx.fillRect(this.x, panelY, this.width, panelHeight);
        this.ctx.strokeRect(this.x, panelY, this.width, panelHeight);

        const centerX = this.x + this.width / 2;
        const centerY = panelY + panelHeight / 2;
        const radius = 7;

        this.ctx.save();
        this.ctx.strokeStyle = '#0078D7';
        this.ctx.lineWidth = 2;
        this.ctx.beginPath();
        this.ctx.arc(centerX, centerY, radius, this.spinnerAngle, this.spinnerAngle + Math.PI * 1.5);
        this.ctx.stroke();
        this.ctx.restore();
    }

    private startSpinnerAnimation(): void {
        if (this.spinnerAnimationFrame !== null) return;
        const tick = () => {
            if (!this.isLoading) {
                this.spinnerAnimationFrame = null;
                return;
            }
            this.spinnerAngle = (this.spinnerAngle + 0.18) % (Math.PI * 2);
            this.levelDesigner.redraw();
            this.spinnerAnimationFrame = requestAnimationFrame(tick);
        };
        this.spinnerAnimationFrame = requestAnimationFrame(tick);
    }

    protected override onSearchTextChanged(): void {
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }

        this.searchTimeout = setTimeout(() => {
            // Always perform search, even with empty text
            this.searchOptions();
        }, 300);
    }

    private async searchOptions(forceAll : boolean = false): Promise<void> {
        this.isLoading = true;
        this.startSpinnerAnimation();
        try {
            const response = await this.apiService.Post<any>(this.url, {
                searchTerm: forceAll ? null : this.searchText,
                PageSize: 10,
                PageNumber: 1
            });

            if(this.isActive === false)
                return;

            if (!response.Results || !Array.isArray(response.Results)) {
                console.error('Invalid response format:', response);
                this.options = [];
                return;
            }

            this.options = response.Results.map((item: any) => {
                const value = item[this.idProperty];
                const label = item[this.labelProperty];
                return {
                    value: value?.toString() || '',
                    label: label || ''
                };
            });

            this.dropdownVisible = true;
        } catch (error) {
            console.error('Failed to search options:', error);
            this.options = [];
        } finally {
            this.isLoading = false;
            this.levelDesigner.redraw();
        }
    }

    override focus(): void {
        super.focus();
        this.isSelected = true;
        this.cursorPosition = this.searchText.length;
        this.ensureCursorVisible();
        this.focusValue = this.value;

        // Always perform an initial search when focused
        this.searchOptions(true);
    }

    override handleClick(x: number, y: number): boolean {
        const valueBefore = this.value;
        const dropdownVisibleBefore = this.dropdownVisible;

        const handled = super.handleClick(x, y);
        if (dropdownVisibleBefore && this.value !== valueBefore) {
            this.originalDisplayValue = this.displayValue;
            this.searchText = this.displayValue;
        }

        return handled;
    }

    override blur(): void {
        super.blur();
        this.isSelected = false;
        this.textOffset = 0;
        this.isLoading = false;
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }
        if (this.spinnerAnimationFrame !== null) {
            cancelAnimationFrame(this.spinnerAnimationFrame);
            this.spinnerAnimationFrame = null;
        }

        if (this.value !== this.focusValue) {
            // A selection was committed during this focus session — adopt the new
            // display as the baseline so subsequent blurs don't revert it.
            this.originalDisplayValue = this.displayValue;
            this.searchText = this.displayValue;
        } else {
            // No selection made — revert any typed text back to the prior display.
            this.displayValue = this.originalDisplayValue;
            this.searchText = this.originalDisplayValue;
        }
    }

    override setValue(value: any): void {
        super.setValue(value);
        // Update the original display value when a new value is selected
        this.originalDisplayValue = String(this.displayValue);
        this.searchText = this.originalDisplayValue;
        this.ensureCursorVisible();
    }
} 
